/**
 * Reduces a loader's long-format rows into daily totals per split and optional bucket.
 * The result is the shared numerical source for Flow charts, KPIs and breakdown tables.
 * Required because all three views must use the same daily values for splits and buckets.
 */
import { CategoryLabelSortFn } from 'src/app/utils/echarts/xy-chart-axis';
import {
    FlowDashboardRow,
    FlowRangeAggregation,
} from './flow-dashboard.models';

export interface FlowGroupAggregate {
    inflowByDate: Record<string, number>;
    outflowByDate: Record<string, number>;
    inflowTotal: number;
    outflowTotal: number;
}

export type FlowBucketAggregate = Record<
    string,
    Record<
        string,
        {
            inflowByDate: Record<string, number>;
            outflowByDate: Record<string, number>;
        }
    >
>;

export interface FlowAggregation {
    dates: string[];
    groups: Record<string, FlowGroupAggregate>;
    byBucket: FlowBucketAggregate;
    bucketValues: string[];
    groupByBucket: boolean;
}

export interface FlowAggregationOptions {
    dateKey?: string;
    inflowKey?: string;
    outflowKey?: string;
    splitKey?: string;
    bucketKey?: string;
    totalLabel?: string;
    unknownBucketLabel?: string;
    /**
     * Comparator for the category axis. Omitted = plain string order, which is already correct for
     * ISO dates; a day-number axis passes a numeric comparator.
     */
    xAxisSortFn?: CategoryLabelSortFn;
}

/** Pure row aggregation shared by the Resource Flow state service and future flow dashboards. */
export function aggregateFlowRows(
    rows: FlowDashboardRow[],
    options: FlowAggregationOptions = {}
): FlowAggregation {
    const dateKey = options.dateKey ?? 'date';
    const inflowKey = options.inflowKey ?? 'inflow';
    const outflowKey = options.outflowKey ?? 'outflow';
    const splitKey = options.splitKey ?? 'split_group';
    const bucketKey = options.bucketKey ?? 'bucket_group';
    const totalLabel = options.totalLabel ?? 'Total';
    const unknownBucketLabel = options.unknownBucketLabel ?? 'Unknown';
    const dates = [...new Set(rows.map((row) => String(row[dateKey])))].sort(
        options.xAxisSortFn
    );
    const hasBucket = rows.some((row) => bucketKey in row);
    const groups: Record<string, FlowGroupAggregate> = {};
    const byBucket: FlowBucketAggregate = {};
    const bucketTotals: Record<string, number> = {};

    for (const row of rows) {
        const groupKey = String(row[splitKey] ?? totalLabel);
        const date = String(row[dateKey]);
        const inflow = +(row[inflowKey] ?? 0) || 0;
        const outflow = +(row[outflowKey] ?? 0) || 0;
        const group = (groups[groupKey] ??= {
            inflowByDate: {},
            outflowByDate: {},
            inflowTotal: 0,
            outflowTotal: 0,
        });
        group.inflowByDate[date] = (group.inflowByDate[date] || 0) + inflow;
        group.outflowByDate[date] = (group.outflowByDate[date] || 0) + outflow;
        group.inflowTotal += inflow;
        group.outflowTotal += outflow;

        if (hasBucket) {
            const rawBucket = row[bucketKey];
            const bucket =
                rawBucket == null || rawBucket === '' || rawBucket === '_NULL_'
                    ? unknownBucketLabel
                    : String(rawBucket);
            const bucketGroup = ((byBucket[bucket] ??= {})[groupKey] ??= {
                inflowByDate: {},
                outflowByDate: {},
            });
            bucketGroup.inflowByDate[date] =
                (bucketGroup.inflowByDate[date] || 0) + inflow;
            bucketGroup.outflowByDate[date] =
                (bucketGroup.outflowByDate[date] || 0) + outflow;
            bucketTotals[bucket] =
                (bucketTotals[bucket] || 0) + inflow + outflow;
        }
    }

    const bucketValues = Object.keys(bucketTotals).sort(
        (a, b) => bucketTotals[b] - bucketTotals[a]
    );
    return {
        dates,
        groups,
        byBucket,
        bucketValues,
        groupByBucket: hasBucket && bucketValues.length > 0,
    };
}

/** Convert ECharts' percentage data zoom into the inclusive set of displayed dates. */
export function getFlowVisibleDates(
    dates: string[],
    zoomStart: number,
    zoomEnd: number
): string[] {
    if (!dates.length) {
        return dates;
    }
    const start = Math.max(
        0,
        Math.round((zoomStart / 100) * (dates.length - 1))
    );
    const end = Math.min(
        dates.length - 1,
        Math.round((zoomEnd / 100) * (dates.length - 1))
    );
    return dates.slice(start, end + 1);
}

/** Reduce a series over a fixed date set, treating an absent date as zero. */
export function aggregateFlowRange(
    byDate: Record<string, number>,
    dates: Iterable<string>,
    mode: FlowRangeAggregation
): number {
    const values = [...dates].map((date) => byDate[date] || 0);
    if (!values.length) {
        return 0;
    }
    const sum = values.reduce((total, value) => total + value, 0);
    return mode === 'sum' ? sum : sum / values.length;
}
